import { ReactNode, Ref, SyntheticEvent, forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccordionDetails,
  AccordionSummary,
  Accordion as MuiAccordion,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';

import { InfoTooltip, Props as InfoTooltipProps } from '@common/components/InfoTooltip';
import { ChevronDownIcon } from '@/assets/icons';

type Props = {
  children: ReactNode;
  title?: ReactNode;
  titleKey?: string;
  titleSx?: SxProps;
  InfoTooltipProps?: Omit<InfoTooltipProps, 'children'>;
  sx?: SxProps;
  onChange?: (expanded: boolean) => void;
  fontSize?: string;
  beforeIcon?: ReactNode;
  afterIcon?: ReactNode;
  expanded?: boolean;
  defaultExpanded?: boolean;
  className?: string;
  unmountOnExit?: boolean;
  expandIcon?: ReactNode;
};
// plz fix me
// eslint-disable-next-line react/display-name
export const Accordion = forwardRef(
  (
    {
      children,
      sx,
      title,
      titleKey,
      InfoTooltipProps,
      onChange,
      titleSx,
      fontSize = '14px',
      afterIcon,
      beforeIcon,
      expanded,
      defaultExpanded,
      className,
      unmountOnExit = false,
      expandIcon = <ChevronDownIcon />,
    }: Props,
    ref: Ref<HTMLDivElement>,
  ) => {
    const { t } = useTranslation();
    
    const summary = (
      <Stack
        direction='row'
        justifyContent='left'
        gap='12px'
        alignItems='center'
        width='100%'
      >
        {beforeIcon}
        <Typography sx={titleSx} component='span'>
          {titleKey ? t(titleKey) : title}
        </Typography>
      </Stack>
    );

    const onInnerChange = useCallback(
      (e: SyntheticEvent, expanded: boolean) => {
        onChange?.(expanded);
      },
      [onChange],
    );

    return (
      <MuiAccordion
        ref={ref}
        sx={sx}
        defaultExpanded={defaultExpanded}
        expanded={expanded}
        onChange={onInnerChange}
        className={className}
        TransitionProps={{ unmountOnExit }}
      >
        <Stack position='relative'>
          <AccordionSummary
            expandIcon={expandIcon}
            sx={{
              '& .MuiTypography-root': { fontSize },
              '& .MuiAccordionSummary-content': {
                paddingRight: afterIcon ? '24px' : undefined,
              },
              minWidth: '0px',
            }}
          >
            {InfoTooltipProps ? <InfoTooltip {...InfoTooltipProps}>{summary}</InfoTooltip> : summary}
          </AccordionSummary>
          {afterIcon && (
            <Stack
              position='absolute'
              right={expandIcon ? '28px' : '8px'}
              top={0}
              height='100%'
              width='32px'
              alignItems='center'
              justifyContent='center'
            >
              {afterIcon}
            </Stack>
          )}
        </Stack>
        <AccordionDetails>{children}</AccordionDetails>
      </MuiAccordion>
    );
  },
);
